const POSITION_TOLERANCE = 2;

class MeriProductScroll extends HTMLElement {
  connectedCallback() {
    this.disconnect();
    this.track = this.querySelector('[data-product-scroll-track]');
    this.controls = this.querySelector('[data-product-scroll-controls]');
    this.previous = this.querySelector('[data-product-scroll-previous]');
    this.next = this.querySelector('[data-product-scroll-next]');
    this.currentLabel = this.querySelector('[data-product-scroll-current]');
    this.totalLabel = this.querySelector('[data-product-scroll-total]');
    if (!this.track || !this.controls || !this.previous || !this.next) return;

    if (this.track.dataset.scrollDesktop === 'false' && this.track.dataset.scrollMobile === 'false') {
      this.controls.hidden = true;
      this.track.tabIndex = -1;
      this.removeAttribute('data-measured');
      return;
    }

    this.events = new AbortController();
    const { signal } = this.events;
    this.previous.addEventListener('click', () => this.move(-1), { signal });
    this.next.addEventListener('click', () => this.move(1), { signal });
    this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
    this.track.addEventListener(
      'scrollend',
      () => {
        if (this.targetIndex != null && Math.abs(this.position - this.stops[this.targetIndex]) > 0.5) {
          this.goTo(this.targetIndex);
          return;
        }
        this.update();
      },
      { signal },
    );
    // New direct input supersedes an outstanding button or keyboard destination.
    for (const type of ['wheel', 'pointerdown'])
      this.track.addEventListener(
        type,
        () => {
          this.targetIndex = null;
        },
        { passive: true, signal },
      );
    this.track.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.track.addEventListener(
      'focusin',
      (event) => {
        // Native focus scrolling must reveal the newly focused product link.
        if (event.target !== this.track) this.targetIndex = null;
      },
      { signal },
    );

    if (globalThis.ResizeObserver) {
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.track);
    } else {
      window.addEventListener('resize', () => this.measure(), { signal });
    }
    this.measure();
    this.prepareCards(signal);
  }

  disconnectedCallback() {
    this.disconnect();
  }

  disconnect() {
    this.events?.abort();
    this.resize?.disconnect();
    this.nearby?.disconnect();
    this.nearby = null;
    if (this.frame != null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.targetIndex = null;
  }

  // 2.9.5 (Owner): a browser starts a lazy card picture only once the card is inside the track's visible width (Chrome
  // gives a scroller no lazy-load margin), so every card swiped in showed its background before its picture. Once the
  // page has loaded and gone idle, a track within a screen of the viewport fetches the pictures of its next screen of
  // cards; the shopper's first move of the track (a swipe, the wheel, an arrow or a key) fetches all the other cards,
  // the next screen first. Fetching every card of every track at idle instead arrived as one burst that held the
  // page's frames while it scrolled, so those wait for the shopper, as a homepage gallery does.
  prepareCards(signal) {
    this.cardStage = 0;
    const idle = () => {
      const run = () => {
        if (!signal.aborted) this.loadCards(false);
      };
      if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2000 });
      else setTimeout(run, 200);
    };
    if (globalThis.IntersectionObserver) {
      // A reconnected scroll has a new observer: a report the old one had already queued must not touch it.
      const nearby = new IntersectionObserver(
        (entries) => {
          if (signal.aborted || !entries.some((entry) => entry.isIntersecting)) return;
          nearby.disconnect();
          if (document.readyState === 'complete') idle();
          else window.addEventListener('load', idle, { once: true, signal });
        },
        { rootMargin: '100% 0px' },
      );
      nearby.observe(this.track);
      this.nearby = nearby;
    }
    this.track.addEventListener('scroll', () => this.loadCards(true), { passive: true, signal });
  }

  // Asks for the pictures of the next screen of cards (as many cards as show now; at the end, the screen before) and,
  // with `all`, for every other card at low priority. A card's hover picture (product-card__secondary) is left to load
  // when its card shows, the only time it can be hovered; on touch screens it is the card picture's own file.
  loadCards(all) {
    const stage = all ? 2 : 1;
    if (this.cardStage >= stage || this.track.scrollWidth - this.track.clientWidth <= POSITION_TOLERANCE) return;
    const items = [...this.track.querySelectorAll(':scope > [data-product-scroll-item]')];
    const view = this.track.getBoundingClientRect();
    const shown = items.filter((item) => {
      const box = item.getBoundingClientRect();
      return box.right > view.left + POSITION_TOLERANCE && box.left < view.right - POSITION_TOLERANCE;
    });
    if (!shown.length) return;
    this.cardStage = stage;
    const first = items.indexOf(shown[0]);
    const last = items.indexOf(shown.at(-1));
    let next = items.slice(last + 1, last + 1 + shown.length);
    if (!next.length) next = items.slice(Math.max(0, first - shown.length), first);
    for (const item of items) {
      const near = next.includes(item);
      if (shown.includes(item) || (!near && !all)) continue;
      for (const image of item.querySelectorAll('img:not(.product-card__secondary)')) {
        if (image.loading !== 'lazy') continue;
        if (all) image.fetchPriority = near ? 'high' : 'low';
        image.loading = 'eager';
        // Like a gallery's neighbouring slide, the next screen is decoded ahead so it shows at once.
        if (near) image.decode?.().catch(() => {});
      }
    }
  }

  get rightToLeft() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  get position() {
    return Math.max(0, this.rightToLeft ? -this.track.scrollLeft : this.track.scrollLeft);
  }

  measure() {
    if (!this.isConnected || !this.track) return;
    const items = [...this.track.querySelectorAll(':scope > [data-product-scroll-item]')];
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const rtl = this.rightToLeft;
    const track = this.track.getBoundingClientRect();
    const initial = items[0]?.getBoundingClientRect();
    const origin = initial ? (rtl ? track.right - initial.right : initial.left - track.left) : 0;
    const stops = [0];

    if (maximum > POSITION_TOLERANCE && items.length > 1) {
      for (const item of items.slice(1)) {
        const rect = item.getBoundingClientRect();
        const inlineOffset = (rtl ? track.right - rect.right : rect.left - track.left) - origin;
        const position = Math.min(maximum, Math.max(0, inlineOffset));
        if (position - stops.at(-1) > POSITION_TOLERANCE) stops.push(position);
      }
      if (maximum - stops.at(-1) > POSITION_TOLERANCE) stops.push(maximum);
    }

    this.stops = stops;
    this.targetIndex = null;
    this.controls.hidden = stops.length < 2 || this.dataset.navigation === 'false';
    this.track.tabIndex = stops.length < 2 ? -1 : 0;
    this.update();
    this.setAttribute('data-measured', '');
  }

  nearestIndex() {
    const position = this.position;
    return this.stops.reduce(
      (best, stop, index) => (Math.abs(stop - position) < Math.abs(this.stops[best] - position) ? index : best),
      0,
    );
  }

  update() {
    if (!this.stops) return;
    const index = this.nearestIndex();
    // update() runs every scroll frame; the counter is a polite live region, so
    // write only when the value changes to avoid repeated announcements.
    const current = String(index + 1);
    const total = String(this.stops.length);
    if (this.currentLabel && this.currentLabel.textContent !== current) this.currentLabel.textContent = current;
    if (this.totalLabel && this.totalLabel.textContent !== total) this.totalLabel.textContent = total;
    const states = [
      [this.previous, index === 0],
      [this.next, index === this.stops.length - 1],
    ];
    const active = document.activeElement;
    for (const [button, disabled] of states) if (button !== active) button.disabled = disabled;
    for (const [button, disabled] of states) {
      if (button !== active) continue;
      // Disabling the focused arrow would drop keyboard focus to the page; hand it to the other arrow or the track.
      if (disabled) {
        const other = states.find(([candidate]) => candidate !== button)[0];
        (other.disabled ? this.track : other).focus({ preventScroll: true });
      }
      button.disabled = disabled;
    }
  }

  scheduleUpdate() {
    if (this.frame != null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  goTo(index) {
    if (!this.stops?.length) return;
    this.targetIndex = Math.min(this.stops.length - 1, Math.max(0, index));
    const position = this.stops[this.targetIndex] * (this.rightToLeft ? -1 : 1);
    this.track.scrollTo({
      left: position,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    this.scheduleUpdate();
  }

  move(direction) {
    this.goTo((this.targetIndex ?? this.nearestIndex()) + direction);
  }

  onKeydown(event) {
    if (event.target !== this.track) return;
    const direction = this.rightToLeft ? -1 : 1;
    if (event.key === 'ArrowRight') this.move(direction);
    else if (event.key === 'ArrowLeft') this.move(-direction);
    else if (event.key === 'Home') this.goTo(0);
    else if (event.key === 'End') this.goTo(this.stops.length - 1);
    else {
      this.targetIndex = null;
      return;
    }
    event.preventDefault();
  }
}

if (!customElements.get('meri-product-scroll')) customElements.define('meri-product-scroll', MeriProductScroll);
